import { Add, Launch } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getPlantingPriceData,
  getRateTypes,
} from "../../slices/productcostseeds.slice";
import { RootState } from "../../store";
import {
  CompositeFilterDescriptor,
  DataResult,
  process,
} from "@progress/kendo-data-query";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DataGrid } from "../../components/DataTable/DataGrid";
import { EditPlantingPriceModal } from "./EditPlantingPriceModal";
import SearchBox from "./SearchBox";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

export const Planting = () => {
  const dispatch = useAppDispatch();
  const [selectedRateType, setSelectedRateType] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilters);
  const exportRef = useRef<ExcelExport | null>(null);
  const { plantingPrices, rateTypesArray } = useAppSelector(
    (state: RootState) => state.seedsdropdown
  );

  const [open, setOpen] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);

  const createEmptyPlantingItem = () => ({
    id: 0,
    rateType: "",
    workName: "",
    machineName: "",
    periods: [],
  });

  const onEditClick = (dataItem: any) => {
    setEditItem(dataItem);
    setOpen(true);
  };

  const gridColumns: any[] = [
    {
      field: "rateType",
      title: "Rate Type",
      width: "180px",
    },
    {
      field: "workName",
      title: "Work Name",
      width: "200px",
    },
    {
      field: "machineName",
      title: "Machine Name",
      width: "200px",
    },
    {
      field: "formattedPeriods",
      title: "Price Periods",
      width: "250px",
    },
    {
      title: "Actions",
      width: "100px",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={onEditClick} />
        ),
      },
    },
  ];

  useEffect(() => {
    dispatch(getPlantingPriceData());
    dispatch(getRateTypes());
  }, []);

  useEffect(() => {
    if (searchQuery || selectedRateType) {
      const filterObj: CompositeFilterDescriptor = {
        logic: "and",
        filters: [],
      };
      if (selectedRateType !== "All") {
        filterObj.filters.push({
          field: "rateType",
          operator: "eq",
          value: selectedRateType,
        });
      }
      if (searchQuery) {
        filterObj.filters.push({
          logic: "or",
          filters: [
            {
              field: "workName",
              operator: "contains",
              value: searchQuery,
            },
            {
              field: "machineName",
              operator: "contains",
              value: searchQuery,
            },
          ],
        });
      }
      setFilter(filterObj);
    } else {
      setFilter(initialFilters);
    }
  }, [searchQuery, selectedRateType]);

  const handleClose = () => {
    dispatch(getPlantingPriceData());
    setOpen(false);
    setIsCreate(false);
    setEditItem(null);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(plantingPrices, {
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  const onAddPlantingPriceClick = () => {
    setIsCreate(true);
    setEditItem(createEmptyPlantingItem());
    setOpen(true);
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <FormControl>
          <InputLabel>Rate Type</InputLabel>
          <Select
            label="Rate Type"
            value={selectedRateType}
            onChange={(e: SelectChangeEvent) =>
              setSelectedRateType(e.target.value)
            }
            size="small"
            sx={{ minWidth: "200px" }}
          >
            <MenuItem value="All">All</MenuItem>
            {rateTypesArray.map((rateObj) => (
              <MenuItem key={rateObj.id} value={rateObj.name}>
                {rateObj.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SearchBox
          placeholder={"Search Work/Machine"}
          searchQuery={searchQuery}
          handleSearchChange={handleSearchChange}
        />
        <Button variant="contained" startIcon={<Add />} onClick={onAddPlantingPriceClick}>
                  Add Planting Cost
                </Button>
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
        >
          Export
        </Button>
      </Box>
      <Box>
        <DataGrid
          ref={exportRef}
          data={plantingPrices}
          dataItemKey="id"
          filter={filter}
          columns={gridColumns}
        />
      </Box>
      {open && (
        <EditPlantingPriceModal
          open={open}
          handleClose={handleClose}
          editItem={editItem}
          isCreate={isCreate}
        />
      )}
    </Box>
  );
};
